import { Injectable, signal } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

//
// Replaces cgBusy ($scope.spinnerGet holding a pending promise). There is exactly one call site
// (the auth + preset fetch chain in startFunction) so a single boolean signal is enough - no need
// for a generic "spinner on every HTTP call" interceptor.
//
// What used to live here as well, and no longer does: isDownloading and downloadProgress, a fixed
// 5s message followed by a 2s spinner. That sequence was honest by construction - it was built as a
// fixed duration precisely so as not to fake a signal nobody had - but it could only ever describe
// ONE download, and it opened by clearing whatever was there, so a second submit erased the first
// download's record. It is replaced by DownloadService, which keeps a register of entries driven by
// real events. See ai/design/concept/20260814_DOWNLOAD_PROGRESS.md.
//
@Injectable({ providedIn: 'root' })
export class LoadingService
{
  readonly isLoading = signal(false);

  //
  // True from application start until the report catalog has settled - loaded, or failed and reported.
  // isLoading alone is not enough for that: it drops to false between the auth call and the catalog
  // call that follows it, and again for as long as the app is waiting on anything that is not itself
  // an HTTP request, which showed the user an empty screen with nothing happening. Cleared by
  // GmrDataService on both the success and the failure path, so the overlay always ends up giving way
  // to either the function list or the error modal.
  //
  readonly awaitingCatalog = signal(true);

  track<T>(source: Observable<T>): Observable<T>
  {
    this.isLoading.set(true);
    return source.pipe(finalize(() => this.isLoading.set(false)));
  }
}
